import React, { useRef, useState } from "react";
import { Tooltip } from "react-tooltip";

import NotificationBlock from "~/components/notificationBlock/NotificationBlock.tsx";
import {
  GoogleAnalyticsPropertyRealtimeReport,
  GoogleAnalyticsPropertyReport,
} from "~/pages/GoogleDashboardPage/types/RenderableWidget.type";
import { formatMetricOrDimensionName } from "~/pages/GoogleDashboardPage/utils/formatMetricOrDimensionName";

import { formatMetricValue } from "../../utils/formatMetricValue";
import styles from "./CardsView.module.scss";

let instanceId = 0;

export const CardsView = ({
  data,
}: {
  data: GoogleAnalyticsPropertyReport | GoogleAnalyticsPropertyRealtimeReport;
}) => {
  const [isAlertHidden, setIsAlertHidden] = useState(false);

  const instanceIdRef = useRef(instanceId++);

  // Only show metrics from the first row
  const firstRow = data.rows?.[0];
  if (!firstRow) return null;

  const hasMultipleRows = data.rows && data.rows.length > 1;

  const getTooltipProps = (
    value: string,
    idx: number,
  ): React.HTMLAttributes<HTMLParagraphElement> => {
    const displayValue = formatMetricValue(
      value,
      data.metric_headers[idx].type,
      true,
    );

    const longValue = formatMetricValue(
      value,
      data.metric_headers[idx].type,
      false,
    );

    if (longValue === displayValue) {
      return {};
    }

    return {
      "data-tooltip-id": `cards-view-tooltip-${instanceIdRef.current}`,
      "data-tooltip-content": longValue,
    };
  };

  return (
    <div
      className={styles.root}
      style={
        {
          "--metric-count": firstRow.metric_values.length,
        } as React.CSSProperties
      }
    >
      {firstRow.metric_values.map(({ value }, idx) => (
        <React.Fragment key={idx}>
          <div className={styles.metric}>
            <h3 className={styles.metricTitle}>
              {formatMetricOrDimensionName(data.metric_headers[idx].name)}
            </h3>
            <p className={styles.metricValue} {...getTooltipProps(value, idx)}>
              {formatMetricValue(value, data.metric_headers[idx].type, true)}
            </p>
          </div>
          {idx < firstRow.metric_values.length - 1 && (
            <div className={styles.divider} />
          )}
        </React.Fragment>
      ))}

      {hasMultipleRows && !isAlertHidden && (
        <NotificationBlock
          type="warning"
          withIcon={false}
          className={styles.warningCard}
        >
          <p>
            Incorrect query detected: multiple rows of data returned, only
            showing data from the first row.
          </p>
          <button
            className={styles.closeButton}
            onClick={() => setIsAlertHidden(true)}
            aria-label="Dismiss warning"
          >
            ×
          </button>
        </NotificationBlock>
      )}
      <Tooltip
        id={`cards-view-tooltip-${instanceIdRef.current}`}
        place="bottom"
      />
    </div>
  );
};
